import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "@/lib/utils";

export const cardStyles = cva(
  "relative flex flex-col rounded-[var(--radius-lg)] text-ds-text-primary transition-[box-shadow,border-color,transform] duration-300 ease-in-out",
  {
    variants: {
      variant: {
        /** Standard surface for grouped content. */
        default: "border border-ds-gold-primary/20 bg-ds-black-tertiary/80 backdrop-blur-md",
        /** Gold frame and glow for featured content. */
        gilded: "border border-ds-gold-primary bg-ds-black-tertiary shadow-ds-gold",
        /** Floats above the page; no visible border. */
        elevated: "border border-white/5 bg-ds-black-secondary shadow-ds-lg",
        /** Black glass with a soft gold rim for secondary or informational content. */
        glass: "border border-ds-gold-primary/30 bg-black/55 backdrop-blur-md",
      },
      padding: {
        none: "",
        sm: "gap-3 p-3",
        md: "gap-4 p-4 md:p-6",
        lg: "gap-6 p-6 md:p-8",
      },
      interactive: {
        true: "cursor-pointer hover:-translate-y-0.5 hover:border-ds-gold-accent hover:shadow-ds-gold focus-visible:outline-none focus-visible:shadow-[var(--focus-ring)]",
      },
    },
    defaultVariants: { variant: "default", padding: "md" },
  },
);

export interface CardProps
  extends React.HTMLAttributes<HTMLElement>, Omit<VariantProps<typeof cardStyles>, "interactive"> {
  /** Lift and glow on hover. Use with `as="a"`/`"button"` or an onClick. */
  interactive?: boolean;
  /** Rendered element. Default "div"; use "article" for standalone content. */
  as?: React.ElementType;
}

/** Base card container. Compose with CardHeader, CardContent and CardFooter. */
export const Card = React.forwardRef<HTMLElement, CardProps>(
  ({ variant, padding, interactive, as: Component = "div", className, ...props }, ref) => (
    <Component
      ref={ref}
      className={cn(cardStyles({ variant, padding, interactive }), className)}
      {...props}
    />
  ),
);
Card.displayName = "Card";
